import { useMemo, useState, type FormEvent } from 'react';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from './IconButton';
import { IconHistory } from './icons';
import { Modal } from './Modal';
import Pagination from './Pagination';
import { formatDateTime } from '../utils/format';
import { labelFor, listOptions, useConfig } from '../context/ConfigContext';
import usePaginatedList from '../hooks/usePaginatedList';

interface EntitlementRow {
  user_id: number;
  user_name: string;
  employee_code: string | null;
  type: string;
  policy_days: number | null;
  adjustment_total: number;
  carried_over_days: number;
  entitled_days: number;
  used_days: number;
  remaining_days: number;
}

interface Adjustment {
  id: number;
  days: number;
  reason: string | null;
  created_by_name: string | null;
  created_at: string;
}

interface Props {
  // GET /leave-entitlements and /leave-entitlement-adjustments only need
  // hr.view, but adding an adjustment needs hr.update — same per-employee
  // edit tier as compensation and other Employee Pay actions.
  canManage: boolean;
}

export function EmployeeLeaveEntitlementsPanel({ canManage }: Props) {
  const { lists } = useConfig();

  const [filters, setFilters] = useState({ search: '', type: '' });
  const [adjusting, setAdjusting] = useState<EntitlementRow | null>(null);
  const [history, setHistory] = useState<Adjustment[] | null>(null);
  const [days, setDays] = useState('');
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  const query = useMemo(() => ({ search: filters.search || undefined, type: filters.type || undefined }), [filters]);
  const { data, loading, error, setPage, reload } = usePaginatedList<EntitlementRow>('/leave-entitlements', query);

  const loadHistory = (row: EntitlementRow) =>
    api
      .get<Adjustment[]>('/leave-entitlement-adjustments', { params: { user_id: row.user_id, leave_type: row.type } })
      .then((res) => setHistory(res.data));

  const openAdjust = (row: EntitlementRow) => {
    setAdjusting(row);
    setHistory(null);
    setDays('');
    setReason('');
    setErrors({});
    loadHistory(row);
  };

  const submitAdjustment = async (e: FormEvent) => {
    e.preventDefault();
    if (!adjusting) return;
    try {
      await api.post('/leave-entitlement-adjustments', {
        user_id: adjusting.user_id,
        leave_type: adjusting.type,
        days,
        reason: reason || undefined,
      });
      setDays('');
      setReason('');
      setErrors({});
      loadHistory(adjusting);
      reload();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  return (
    <>
      <div className="toolbar">
        <input
          type="search"
          placeholder="Search by name…"
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
        />
        <select value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })}>
          <option value="">All types</option>
          {listOptions(lists, 'leave_type').map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </select>
      </div>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Employee Code</th>
                <th>Name</th>
                <th>Type</th>
                <th>Entitled</th>
                <th>Used</th>
                <th>Remaining</th>
                {canManage && <th></th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={canManage ? 7 : 6} className="muted center">
                    Loading…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={canManage ? 7 : 6} className="error center">
                    {error}
                  </td>
                </tr>
              ) : data && data.data.length > 0 ? (
                data.data.map((row) => (
                  <tr key={`${row.user_id}-${row.type}`}>
                    <td className="muted">{row.employee_code ?? '—'}</td>
                    <td>
                      <strong>{row.user_name}</strong>
                    </td>
                    <td>{labelFor(lists, 'leave_type', row.type)}</td>
                    <td>
                      {row.entitled_days}
                      {row.adjustment_total !== 0 && <small className="muted"> (adjusted)</small>}
                      {row.carried_over_days > 0 && <small className="muted"> (+{row.carried_over_days} carried over)</small>}
                    </td>
                    <td>{row.used_days}</td>
                    <td>{row.remaining_days}</td>
                    {canManage && (
                      <td className="right">
                        <IconButton icon={IconHistory} label="Adjust entitlement" onClick={() => openAdjust(row)} />
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={canManage ? 7 : 6} className="muted center">
                    No employees found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination meta={data} onPage={setPage} />
      </div>

      {adjusting && (
        <Modal title={`Adjust entitlement — ${adjusting.user_name}`} onClose={() => setAdjusting(null)}>
          <p className="muted">
            {labelFor(lists, 'leave_type', adjusting.type)} — position default: {adjusting.policy_days ?? 'none configured'} days.
            Current entitlement: <strong>{adjusting.entitled_days}</strong> days.
          </p>

          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Days</th>
                  <th>Reason</th>
                  <th>By</th>
                </tr>
              </thead>
              <tbody>
                {history === null ? (
                  <tr>
                    <td colSpan={4} className="muted center">
                      Loading…
                    </td>
                  </tr>
                ) : history.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="muted center">
                      No adjustments yet.
                    </td>
                  </tr>
                ) : (
                  history.map((a) => (
                    <tr key={a.id}>
                      <td className="muted">{formatDateTime(a.created_at)}</td>
                      <td>{a.days > 0 ? `+${a.days}` : a.days}</td>
                      <td>{a.reason ?? '—'}</td>
                      <td className="muted">{a.created_by_name ?? '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <form onSubmit={submitAdjustment} className="form-grid" style={{ marginTop: 16 }}>
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label>
              Adjustment (+/-) *
              <input type="number" step="0.5" value={days} onChange={(e) => setDays(e.target.value)} placeholder="e.g. 5 or -2" required />
              {errors.days && <small className="error">{errors.days[0]}</small>}
            </label>
            <label>
              Reason
              <input value={reason} onChange={(e) => setReason(e.target.value)} />
              {errors.reason && <small className="error">{errors.reason[0]}</small>}
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setAdjusting(null)}>
                Close
              </button>
              <button className="btn btn-primary">Add Adjustment</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
